// «Кто повторит» режима «Повторить движение» (13.10): сколько людей в ролике — столько слотов, слева направо, как они
// стоят в кадре (владелец: «добавление аватара, а не сразу отображение коллекции»). Слот открывает меню «Загрузить /
// Из моих генераций / Аватар» (тот же пикер, что у слота фото; «Аватар» — окно аватаров). Один человек у нейросети,
// которая понимает указание, — выбор «кого заменить». «Где будет» — как в ролике (замена, переделка Seedance) или как
// на фото (Kling Motion Control). Нейросеть выбирается на штатном месте — пилюлей в баре (WidgetModelPicker).
// 14.10: сервер находит людей в ролике (services/motion_people) — тогда слот стоит у каждого человека (плитка-кадр и
// подпись), аватар кладут на конкретного; людей может быть и десять — аватаров не больше, чем нейросеть заменит,
// остальные остаются как есть. Распознавание ошибается — «По порядку» возвращает слоты по счёту слева направо.
// 19.10: у Wan и Kling наш выбор «кого» не уходит (сеть берёт самого заметного сама) — активна только первая плитка,
// остальные приглушены с объяснением (motionTool.autoTarget). «Где будет» из тела убрано (владелец: «нейронка не должна
// меняться кнопкой выбора») — группы «Как в ролике» / «Как на фото» живут в списке пилюли нейросети.
// 18.10 (владелец: «мне понравились такие слоты, сделаем во всех версиях»): люди — плитками-кадрами сеткой, кружок
// аватара в углу плитки, подпись под ней; десять человек — два ряда на телефоне (стили .gtm__who-people в _tool-mode).
// Тап по всей плитке открывает выбор аватара (владелец: «событие по всему слоту»), в шапке меню — кадр и подпись
// человека, на десктопе кадр крупнее при наведении. Без ролика колонка пуста: люди, слоты и запасной «Кого заменить»
// (когда людей не распознали, а аватар один) — после загрузки ролика. Пояснений вроде
// «положите аватара на того, кого заменить» нет (владелец 18.10: нативный виджет без очевидных описаний в центре) —
// остаются только статус ожидания, предупреждения и ссылка «Люди не те? По порядку».
import { Plus, X } from 'lucide-react'
import NumberStepper from '../../../components/NumberStepper'
import { Icon } from '../../../../public/icons/Icon'
import Tooltip from '../../../components/Tooltip'
import { useGenerate } from '../../../context/GenerateContext'
import { REPLACE_TARGETS } from './motionTool'
import type { MotionMode, MotionSlot } from './useMotionMode'
// Замен за раз, с которых Seedance заметно чаще путает людей (прогоны владельца 13–14.10 с четырьмя аватарами).
const MANY_SWAPS = 4

function slotThumb(s: NonNullable<MotionSlot>): string | null {
	return s.kind === 'avatar' ? s.who.avatar || s.who.url : s.file.preview || s.file.url || null
}
function slotName(s: NonNullable<MotionSlot>): string {
	return s.kind === 'avatar' ? s.who.name : 'Ваше фото'
}

export default function MotionWhoZone({ motion, onSlotMenu, disabled }: {
	motion: MotionMode
	/** Клик по слоту: меню источника у кнопки (makeShowPicker слота фото) — загрузка, мои генерации, аватар.
	 *  who — человек из ролика (кадр и подпись) для шапки меню «кто встанет на место». */
	onSlotMenu: (button: HTMLElement, index: number, who?: { image?: string | null; label: string }) => void
	disabled: boolean
}) {
	const { fileInputRef, handleImageUpload } = useGenerate()
	const { count, max, slots, target, people, peopleMode, peopleStatus, assigned, autoTarget: auto } = motion
	const names = slots.filter((s): s is NonNullable<MotionSlot> => !!s).map(slotName)
	// Блок идёт по шагам (владелец 18.10: «некоторые поля нет смысла давать использовать, если нет загруженного
	// видео»): без ролика колонка пуста; люди, слоты и «Кого заменить» — после загрузки.
	const hasClip = !!motion.clip
	// Пока ролик грузится и сервер ищет людей — только строка ожидания: степпер и слоты «по порядку» нужны лишь когда
	// людей не распознали (владелец 18.10). Людей ищем при любой нейросети — лимит сети виден на плитках.
	const waiting = hasClip && !motion.peopleOff && (!motion.clip?.url || peopleStatus === 'loading')

	// Почему плитка заблокирована — по условию нейросети (владелец 18.10: «мьютить и блокировать с объяснением»):
	// Kling повторяет движение самого заметного, Wan заменяет его же, у остальных — предел числа замен.
	const autoNote = motion.kind === 'motion'
		? 'Эта нейросеть повторяет движение самого крупного человека в кадре'
		: 'Эта нейросеть заменяет самого крупного человека в кадре, остальные останутся как есть'
	const limitTitle = auto ? autoNote : `Эта нейросеть заменяет не больше ${max} человек`
	const slot = (i: number, title: string, blocked = false, who?: { image?: string | null; label: string }) => {
		const s = slots[i]
		return (
			<div className={`gtm__who-slot${s ? ' is-filled' : ''}`}>
				<button
					type="button"
					className="gtm__who-face"
					onClick={(e) => onSlotMenu(e.currentTarget, i, who)}
					disabled={disabled || blocked}
					title={s ? `${slotName(s)} — заменить` : blocked ? limitTitle : title}
					aria-label={s ? `${slotName(s)}, слот ${i + 1}` : `Слот ${i + 1}: выбрать аватара или фото`}
					aria-haspopup="menu"
					// В режиме людей кружок дублирует плитку (она же кнопка): одна остановка Tab на человека.
					tabIndex={who ? -1 : undefined}
				>
					{s && slotThumb(s) ? <img src={slotThumb(s) ?? undefined} alt="" /> : <Plus size={16} aria-hidden="true" />}
				</button>
				{!peopleMode && count > 1 && <span className="gtm__who-num" aria-hidden="true">{i + 1}</span>}
				{s && !disabled && (
					<button type="button" className="gtm__who-x" onClick={() => motion.clearSlot(i)} aria-label={`Убрать ${slotName(s)}`} title="Убрать">
						<X size={11} />
					</button>
				)}
			</div>
		)
	}

	return (
		<div className="gtm__who">
			{/* Без ролика колонка людей пуста (владелец 18.10: описания в центре виджета только едят место). */}
			{!hasClip ? null : waiting ? (
				<>
					<div className="gtm__who-k">Кто в ролике</div>
					<div className="gtm__who-status" role="status">
						<Icon id="gen-spin" size={12} className="spin" /> {motion.clip?.url ? 'Ищем людей в ролике…' : 'Загружаем ролик…'}
					</div>
				</>
			) : peopleMode ? (
				<>
					<div className="gtm__who-k">Кто в ролике</div>
					<div className="gtm__who-people" role="group" aria-label="Кто в ролике">
						{people.map((p, i) => {
							const s = slots[i]
							// Приглушена: лимит замен исчерпан, либо сеть берёт самого заметного сама (все плитки, кроме первой).
							const blocked = !s && (assigned >= max || (auto && i !== 0))
							const who = { image: p.crop_url ?? null, label: p.label }
							// Плитка целиком — кнопка выбора (владелец 18.10: «событие по всему слоту, а не по маленькому кружку»);
							// кружок в углу — тот же выбор. Кадр крупнее — при наведении (десктоп) и в шапке меню выбора.
							const tile = (
								<button
									type="button"
									className="gtm__who-person"
									onClick={(e) => onSlotMenu(e.currentTarget, i, who)}
									disabled={disabled || blocked}
									title={blocked ? limitTitle : s ? `${p.label} → ${slotName(s)} — заменить` : `Кто встанет на место: ${p.label}`}
									aria-label={s ? `${p.label}: ${slotName(s)}, заменить` : `${p.label}: выбрать аватара или фото`}
									aria-haspopup="menu"
								>
									{p.crop_url ? <img src={p.crop_url} alt="" loading="lazy" decoding="async" /> : <span>{i + 1}</span>}
								</button>
							)
							return (
								<div key={p.id} className={`gtm__who-pair${s ? ' is-filled' : ''}`}>
									{p.crop_url ? <Tooltip imagePreview={p.crop_url}>{tile}</Tooltip> : tile}
									<span className="gtm__who-arrow" aria-hidden="true">→</span>
									{slot(i, `Кто встанет на место: ${p.label}`, blocked, who)}
									{/* Подпись под плиткой (до двух строк): кто в ролике, после назначения — имя аватара; полная пара — в title. */}
									<span className="gtm__who-plabel" title={s ? `${p.label} → ${slotName(s)}` : p.label}>{s ? slotName(s) : p.label}</span>
								</div>
							)
						})}
					</div>
					{/* Пояснений нет (владелец 18.10): плитка с «+» и подпись говорят сами. Остаётся только условие нейросети:
					    сеть берёт самого заметного сама (Wan, Kling) или людей в ролике больше, чем она заменит (лишние плитки
					    после назначения приглушены). */}
					{(auto ? people.length > 1 : people.length > max) && (
						<div className="gtm__who-hint">{auto ? `${autoNote}.` : `Эта нейросеть заменяет до ${max} человек, остальные останутся как есть.`}</div>
					)}
					{assigned >= MANY_SWAPS && motion.kind === 'edit' && (
						<div className="gtm__who-hint">Чем больше замен за раз, тем чаще нейросеть путает людей.</div>
					)}
					<button type="button" className="gtm__who-link" onClick={() => motion.setPeopleOff(true)}>Люди не те? По порядку</button>
				</>
			) : (
				<>
					<div className="gtm__who-k">Людей в ролике</div>
					<div className="gtm__who-count">
						{max > 1 ? (
							<NumberStepper value={count} onChange={(v) => motion.setCount(v ?? 1)} min={1} max={max} disabled={disabled} ariaLabel="Сколько людей заменить" />
						) : (
							<span className="gtm__who-count-one">1</span>
						)}
						<span className="gtm__who-limit">{max > 1 ? `до ${max} у этой нейросети` : 'эта нейросеть берёт одного'}</span>
					</div>
					<div className="gtm__who-k">Кто повторит</div>
					<div className="gtm__who-slots" role="group" aria-label="Кто повторит">
						{slots.map((_, i) => <div key={i} className="gtm__who-slotwrap">{slot(i, count > 1 ? `Человек ${i + 1} слева` : 'Аватар или своё фото')}</div>)}
					</div>
					<div className="gtm__who-name">
						<span>{names.length ? names.join(', ') : count > 1 ? 'Слева направо, как стоят в ролике' : 'Выберите аватара или фото'}</span>
					</div>
					{/* Людей не нашли (сбой разбора, ролика уже нет в хранилище, в кадре никого): говорим прямо, иначе один слот
					    по умолчанию читается как «добавляй сюда всех» (владелец 14.10). */}
					{motion.clip?.url && !motion.peopleOff && (peopleStatus === 'failed' || (peopleStatus === 'done' && people.length === 0)) && (
						<div className="gtm__who-hint" role="status">Людей в ролике не нашли — укажите, сколько их.</div>
					)}
					{motion.peopleOff && people.length > 0 && (
						<button type="button" className="gtm__who-link" onClick={() => motion.setPeopleOff(false)}>Показать людей из ролика</button>
					)}
				</>
			)}
			{hasClip && !waiting && motion.targetChoosable && (
				<>
					<div className="gtm__who-k">Кого заменить</div>
					<div className="gtm__who-seg gtm__who-target" role="group" aria-label="Кого заменить">
						{REPLACE_TARGETS.filter((t) => !t.editOnly || motion.kind === 'edit').map((t) => (
							<button
								key={t.key}
								type="button"
								className={target === t.key ? 'is-on' : ''}
								aria-pressed={target === t.key}
								disabled={disabled}
								onClick={() => motion.setTarget(t.key)}
							>
								{t.label}
							</button>
						))}
					</div>
				</>
			)}
			{/* Скрытый input слота фото: «Загрузить» в меню слота кликает по нему; канал (своё фото или референсы) выбирает
			    handleImageUpload по флагам нейросети, а в слот фото ставит useMotionMode. */}
			<input
				ref={fileInputRef}
				type="file"
				accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
				onChange={handleImageUpload}
				style={{ display: 'none' }}
				aria-hidden="true"
				tabIndex={-1}
			/>
		</div>
	)
}
